/**
 * ItemsNowInSageBadge — the "waited on item setup, now keyable" draft marker.
 *
 * Merlin drafted the order but some lines had no Sage item (a rep's order for
 * products not yet set up). The hourly draft-item-resolution sweep wrote the
 * codes onto those lines once the items appeared in Sage and re-tiered them
 * to VERIFY (kanban 9c2ab40c). Without this marker the row reads exactly as
 * it did the day it arrived and gets skipped again (NORTH12 PO Verbal Zach
 * sat 8 days after its seven Aervoe PC items were created).
 */
import { hasResolvedItems, type OrderRecord } from '../../types/orders';

interface ItemsNowInSageBadgeProps {
  order: Pick<OrderRecord, 'id' | 'status' | 'llm_extraction_data'>;
}

export default function ItemsNowInSageBadge({ order }: ItemsNowInSageBadgeProps) {
  if (!hasResolvedItems(order)) return null;
  const evidence = order.llm_extraction_data?.item_resolution;
  const codes = (evidence?.resolved ?? []).map((r) => r.item_code).filter(Boolean);
  const remaining = evidence?.remaining_blocked ?? 0;
  const ready = remaining === 0;
  const title =
    `Sage item${codes.length === 1 ? '' : 's'} ${codes.join(', ')} ` +
    `${codes.length === 1 ? 'was' : 'were'} created after this draft and written onto the blocked line${codes.length === 1 ? '' : 's'} automatically. ` +
    (ready
      ? 'Confirm each item and its unit price on the review card, then submit.'
      : `${remaining} line${remaining === 1 ? ' still has' : 's still have'} no Sage item.`);
  return (
    <span
      data-testid={`items-now-in-sage-badge-${order.id}`}
      title={title}
      style={{
        background: ready ? '#ecfdf5' : '#fefce8',
        color: ready ? '#065f46' : '#854d0e',
        border: `1px solid ${ready ? '#6ee7b7' : '#fde047'}`,
        borderRadius: '4px',
        padding: '1px 6px',
        fontSize: '10px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        letterSpacing: '0.02em',
      }}
    >
      {ready ? 'ITEMS NOW IN SAGE' : 'SOME ITEMS NOW IN SAGE'}
    </span>
  );
}
